<template>
  <Loader :value="promises.fetch" theme="default">
    <div class="project-residential-page">
      <div class="project-residential-page__header section__header">
        <ResidentialCover />
        <ResidentialSummary />
      </div>
      <section class="section section__info">
        <div class="container">
          <div class="grow-1">
            <ResidentialOpenhouse v-if="showOpenhouse" class="mb-6" />
            <ResidentialRoles />
          </div>
          <ResidentialInfo />
        </div>
      </section>
      <section class="section section__properties">
        <div class="container">
          <ScrollAnchor value="properties" offset="-8rem" :delay="750" />
          <ResidentialProperties />
        </div>
      </section>
      <section
        v-if="$path('media.floorplan.length', residential)"
        class="section section__floorplan"
      >
        <div class="container">
          <ScrollAnchor value="floorplan" offset="-8rem" :delay="750" />
          <ResidentialFloorplan />
        </div>
      </section>
      <section
        v-if="$path('shop.published', residential) && !hideShop"
        class="section section__shop"
      >
        <ScrollAnchor value="shop" offset="-8rem" :delay="750" />
        <Shop :id="residential.shop.id" />
      </section>
      <section class="section__additional-info">
        <div class="container">
          <ScrollAnchor value="additional-info" offset="-8rem" :delay="750" />
          <ProjectAdditionalInfo />
        </div>
      </section>
      <section class="section section__residential-templates">
        <div class="container">
          <ScrollAnchor value="residential-templates" offset="-8rem" :delay="750" />
          <ProjectResidentialTemplates />
        </div>
      </section>
      <section class="section section__lead">
        <div class="container">
          <div class="row wrap g-6">
            <ScrollAnchor value="lead" offset="-8rem" :delay="750" />
            <ResidentialLead class="grow-1" />
          </div>
        </div>
      </section>
      <section
        class="section section__attachments"
        v-if="
          $path('media.attachments.length', residential) ||
            $path('project.media.attachments.length', residential)
        "
      >
        <div class="container">
          <ScrollAnchor value="attachments" offset="-8rem" :delay="750" />
          <ResidentialAttachments />
        </div>
      </section>
      <section
        v-if="$path('media.gallery.length', residential)"
        class="section section__images section--no-pb"
      >
        <ScrollAnchor value="gallery" offset="-8rem" :delay="750" />
        <ResidentialMasonry />
      </section>
      <section class="section section__map section--no-p">
        <ScrollAnchor value="map" offset="-8rem" :delay="750" />
        <ResidentialMap />
      </section>
    </div>
  </Loader>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import ProjectComponents from '@/components/Project'
import ScrollAnchor from '@kvass/scroll-anchor'
import ResidentialComponents from '@/components/Residential'
import Residentials from '@/components/Residentials'
import Shop from '@kvass/shop'
import BrowserApiMixin from '@/mixins/browser-api'

const PrefixKeys = (prefix, obj) =>
  Object.keys(obj).reduce((acc, key) => ({ ...acc, ...{ [prefix + key]: obj[key] } }), {})

export default {
  mixins: [BrowserApiMixin('project-residential')],
  props: {
    id: String,
  },
  watch: {
    id: {
      handler(val) {
        return this.fetch(val).then(data => {
          if (!data) return this.$router.push({ name: 'notfound' })
        })
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState('Project', {
      item: 'item',
      promises: 'promises',
      residential: state => (state.item.residentials || [])[0] || {},
    }),
    hideShop() {
      return KvassConfig.get('siteSettings.hideShop')
    },
    showOpenhouse() {
      if (!this.item.id) return false
      if (this.$path('item.openhouses.length')) return true
      return !KvassConfig.get('siteSettings.hideNoOpenhouse')
    },
  },
  methods: {
    ...mapActions('Project', ['fetch']),
    gotoLead() {
      document.querySelector('#lead').scrollIntoView({
        behavior: 'smooth',
      })
    },
  },
  metaInfo() {
    return {
      title: this.item.name || `${this.$t('loading', { resource: null })}...`,
    }
  },
  components: {
    ...PrefixKeys('Residential', ResidentialComponents),
    ...PrefixKeys('Project', ProjectComponents),
    Residentials,
    Shop,
    ScrollAnchor,
  },
}
</script>

<style lang="scss">
.project-residential-page {
  @for $i from 1 through 15 {
    & > *:nth-child(#{$i}) {
      order: $i * 10;
    }
  }

  &__header {
    display: flex;

    @include respond-below('phone') {
      flex-direction: column;
    }

    .residential-cover__wrapper,
    .summary {
      flex-basis: 50%;
      flex-grow: 1;
      @include respond-below('phone') {
        flex-basis: initial;
      }
    }
  }

  .section {
    &--no-pb {
      padding-bottom: 0;
    }

    &--no-p {
      padding-top: 0;
      padding-bottom: 0;
    }

    &__shop {
      min-height: 100vh;
    }

    &__info {
      .container {
        display: grid;
        grid-gap: 6rem;
        grid-template-columns: repeat(2, 1fr);

        @include respond-below('phone') {
          grid-template-columns: 1fr;

          & > *:first-child {
            order: 2;
          }

          & > *:nth-child(2) {
            order: 1;
          }
        }
      }
    }
  }

  .cta {
    background-color: $success;
  }
}
</style>
